const API_BASE = '/api/v1/parameters';

export interface LabParameter {
  id: number;
  parameterCode: string;
  parameterName: string;
  value: number;
  unit: string;
  referenceMin: number | null;
  referenceMax: number | null;
  referenceRange: string | null;
  isWithinRange: boolean;
  section: string | null;
  testDate: string;
  labName: string | null;
  notes: string | null;
  sourceFile?: {
    id: number;
    originalFilename: string;
  } | null;
}

export interface ParameterCode {
  code: string;
  name: string;
  count: number;
  firstDate: string;
  lastDate: string;
  latest: {
    value: number;
    unit: string;
    referenceMin: number | null;
    referenceMax: number | null;
    isWithinRange: boolean;
    testDate: string;
  } | null;
}

export interface TrendData {
  code: string;
  name: string;
  data: Array<{
    id: number;
    value: number;
    unit: string;
    referenceMin: number | null;
    referenceMax: number | null;
    isWithinRange: boolean;
    testDate: string;
    labName: string | null;
    sourceFile?: {
      id: number;
      originalFilename: string;
    } | null;
  }>;
  stats: {
    min: number;
    max: number;
    avg: number;
    count: number;
    outOfRangeCount: number;
  };
  referenceRange: {
    min: number | null;
    max: number | null;
    unit: string;
  };
}

export interface ParameterSummary {
  totalParameters: number;
  uniqueParameterTypes: number;
  outOfRangeCount: number;
  recentTestDates: string[];
}

export async function getParameters(options?: {
  code?: string;
  from?: string;
  to?: string;
  outOfRange?: boolean;
  page?: number;
  limit?: number;
}): Promise<{ success: boolean; parameters: LabParameter[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
  const params = new URLSearchParams();
  if (options?.code) params.set('code', options.code);
  if (options?.from) params.set('from', options.from);
  if (options?.to) params.set('to', options.to);
  if (options?.outOfRange) params.set('outOfRange', 'true');
  if (options?.page) params.set('page', options.page.toString());
  if (options?.limit) params.set('limit', options.limit.toString());

  const response = await fetch(`${API_BASE}?${params}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch parameters');
  }

  return response.json();
}

export async function getParameterCodes(): Promise<{ success: boolean; codes: ParameterCode[] }> {
  const response = await fetch(`${API_BASE}/codes`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch parameter codes');
  }

  return response.json();
}

export async function getParameterTrend(
  code: string,
  options?: { from?: string; to?: string }
): Promise<{ success: boolean } & TrendData> {
  const params = new URLSearchParams();
  if (options?.from) params.set('from', options.from);
  if (options?.to) params.set('to', options.to);

  const response = await fetch(`${API_BASE}/trend/${encodeURIComponent(code)}?${params}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch parameter trend');
  }

  return response.json();
}

export async function getLatestParameters(): Promise<{
  success: boolean;
  parameters: LabParameter[];
  bySection: Record<string, LabParameter[]>;
  total: number;
}> {
  const response = await fetch(`${API_BASE}/latest`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch latest parameters');
  }

  return response.json();
}

export async function getOutOfRangeParameters(): Promise<{
  success: boolean;
  parameters: Array<LabParameter & { history: Array<{ value: number; testDate: string; isWithinRange: boolean }> }>;
  total: number;
}> {
  const response = await fetch(`${API_BASE}/out-of-range`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch out-of-range parameters');
  }

  return response.json();
}

export async function getParameterSummary(): Promise<{ success: boolean; summary: ParameterSummary }> {
  const response = await fetch(`${API_BASE}/summary`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch parameter summary');
  }

  return response.json();
}

export async function updateParameter(
  id: number,
  notes: string
): Promise<{ success: boolean; parameter: LabParameter }> {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes }),
  });

  if (!response.ok) {
    throw new Error('Failed to update parameter');
  }

  return response.json();
}

export async function deleteParameter(id: number): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to delete parameter');
  }

  return response.json();
}

export async function getTestDates(): Promise<{
  success: boolean;
  dates: Array<{ date: string; count: number }>;
  total: number;
}> {
  const response = await fetch(`${API_BASE}/dates`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch test dates');
  }

  return response.json();
}

export async function getParametersByDate(date: string): Promise<{
  success: boolean;
  parameters: LabParameter[];
  bySection: Record<string, LabParameter[]>;
  total: number;
}> {
  const response = await fetch(`${API_BASE}/by-date/${date}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch parameters by date');
  }

  return response.json();
}

export async function exportParameters(options?: {
  code?: string;
  from?: string;
  to?: string;
}): Promise<void> {
  const params = new URLSearchParams();
  if (options?.code) params.set('code', options.code);
  if (options?.from) params.set('from', options.from);
  if (options?.to) params.set('to', options.to);

  const response = await fetch(`${API_BASE}/export?${params}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to export parameters');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'parametri.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export async function exportParametersPdf(options?: {
  code?: string;
  from?: string;
  to?: string;
}): Promise<void> {
  const params = new URLSearchParams();
  if (options?.code) params.set('code', options.code);
  if (options?.from) params.set('from', options.from);
  if (options?.to) params.set('to', options.to);

  const response = await fetch(`${API_BASE}/export-pdf?${params}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to export parameters as PDF');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'report_laboratorio.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('it-IT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function formatValue(value: number, unit: string): string {
  return `${value.toFixed(2)} ${unit}`;
}
